import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import AddInventoryModal from "./AddInventoryModal";
import AddWarehouseModal from "./AddWarehouseModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import { FaExchangeAlt, FaPlus } from "react-icons/fa";
import axios from "axios";
import EditInventoryModal from "./EditInventoryModal";
import TransferInventoryModal from "./TransferInventoryModal";
import FilterDropdowns from "./FilterDropdowns";
import WarehouseDetailsModal from "./WarehouseDetailsModal";
import ImageModal from "../../Sales Executive/Items Master/ImageModal";
import { capitalizeWords } from "../../ui/capitalizeWords";
import Swal from "sweetalert2";
import { ColorRing } from "react-loader-spinner";

const WarehouseInventoryTable = () => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [showAddInventoryModal, setShowAddInventoryModal] = useState(false);
  const [showAddWarehouseModal, setShowAddWarehouseModal] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseLoading, setWarehouseLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [warehouseToEdit, setWarehouseToEdit] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedItemName, setSelectedItemName] = useState("");
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const userRole = localStorage.getItem("role");

  const handleTransferClick = () => {
    setShowTransferModal(true);
  };

  const handleTransfer = () => {
    fetchItems();
  };

  useEffect(() => {
    fetchItems();
    fetchWarehouses();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await fetch(buildApiUrl("/api/warehouse/inventory"), {
        method: "GET",
        headers: headers,
      });
      const data = await response.json();
      if (data.data && Array.isArray(data.data)) {
        setItems(data.data);
      } else {
        setError("Invalid response format. Expected an array.");
      }
    } catch (error) {
      setError("An error occurred while fetching inventory items.");
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    setWarehouseLoading(true);
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await fetch(
        buildApiUrl("/api/warehouse/get-warehouse/name"),
        {
          method: "GET",
          headers: headers,
        }
      );
      const data = await response.json();
      if (data.data && Array.isArray(data.data)) {
        setWarehouses(data.data);
      } else {
        setError("Failed to fetch warehouse data.");
      }
    } catch (error) {
      setError("An error occurred while fetching warehouse data.");
    } finally {
      setWarehouseLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredItems = items.filter((item) => {
    const warehouseMatch =
      selectedWarehouse === "" || item.warehouseName === selectedWarehouse;
    const categoryMatch =
      selectedCategory === "" || item.itemCategory === selectedCategory;
    const itemNameMatch =
      selectedItemName === "" || item.itemName === selectedItemName;
    return warehouseMatch && categoryMatch && itemNameMatch;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleAddInventory = async (newItems) => {
    try {
      if (Array.isArray(newItems)) {
        setItems((prevItems) => [...prevItems, ...newItems]);
      }
      await fetchItems();
    } catch (error) {}
  };

  const handleAddWarehouse = (newWarehouse) => {
    fetchWarehouses();
  };

  const handleEditInventory = (item) => {
    setItemToEdit(item);
    setShowEditModal(true);
  };

  const handleDeleteInventory = async (item) => {
    const token = localStorage.getItem("token");
    const itemId = item._id;

    if (!itemId || !token) {
      toast.error("Item ID or token is missing.");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          buildApiUrl(`/api/warehouse/inventory/${itemId}`),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200 || response.status === 204) {
          toast.success("Item deleted successfully.");
          setItems((prevItems) => prevItems.filter((i) => i._id !== itemId));
        } else {
          toast.error("Failed to delete item.");
        }
      } catch (error) {
        toast.error("An error occurred while deleting the item.");
      }
    } else {
      toast.info("Item deletion canceled.");
    }
  };

  const handleEditWarehouse = (warehouse) => {
    setWarehouseToEdit(warehouse);
    setIsEditing(true);
    setShowAddWarehouseModal(true);
  };

  const handleDeleteWarehouse = async (warehouseId) => {
    const token = localStorage.getItem("token");

    if (!warehouseId || !token) return;

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          buildApiUrl(`/api/warehouse/new-warehouse/${warehouseId}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200 || response.status === 204) {
          toast.success("Warehouse deleted");
          setWarehouses((prev) => prev.filter((w) => w._id !== warehouseId));
        } else {
          toast.error("Failed to delete warehouse");
        }
      } catch (error) {
        toast.error("Error deleting warehouse");
      }
    } else {
      toast.info("Warehouse deletion canceled");
    }
  };

  const handleUpdateWarehouse = async (updatedWarehouse) => {
    setWarehouses((prev) =>
      prev.map((warehouse) =>
        warehouse._id === updatedWarehouse._id ? updatedWarehouse : warehouse
      )
    );
    await fetchItems();
  };

  const handleUpdateInventory = (updatedItem) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.itemCode === updatedItem.itemCode ? updatedItem : item
      )
    );
    setShowEditModal(false);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === "warehouse") {
      setSelectedWarehouse(value);
    } else if (filterType === "category") {
      setSelectedCategory(value);
    } else if (filterType === "itemName") {
      setSelectedItemName(value);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage("");
  };

  return (
    <div className="bg-white">
      <div className="">
        {/* Header with Filters and Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-[repeat(3,_1fr)_repeat(4,_max-content)] gap-2 sm:items-center w-full">
            <FilterDropdowns
              warehouses={warehouses}
              items={items}
              onFilterChange={handleFilterChange}
              selectedCategory={selectedCategory}
              selectedWarehouse={selectedWarehouse}
              selectedItemName={selectedItemName}
              className="w-full sm:w-1/3"
            />
            <button
              onClick={() => setShowAddInventoryModal(true)}
              className="bg-indigo-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-indigo-800 transition-all duration-300 ease-in-out"
            >
              <FaPlus size={12} />
              Open Stock
            </button>
            <button
              onClick={() => setShowAddWarehouseModal(true)}
              className="bg-blue-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-blue-800 transition-all duration-300 ease-in-out"
            >
              <FaPlus size={12} />
              Warehouse
            </button>
            <button
              onClick={handleTransferClick}
              className="bg-yellow-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-yellow-800 transition-all duration-300 ease-in-out"
            >
              <FaExchangeAlt size={12} />
              Trans. Stock
            </button>
            <button
              onClick={() => setShowWarehouseModal(true)}
              className="bg-purple-600 flex gap-1 justify-center items-center text-white px-3 py-2 text-xs font-medium w-full sm:w-fit rounded-md shadow-lg hover:bg-purple-800 transition-all duration-300 ease-in-out"
            >
              Show Warehouses
            </button>
          </div>
        </div>

        {/* Loading/Error State */}
        {loading ? (
          <div className="flex justify-center items-center">
            <ColorRing
              visible={true}
              height="80"
              width="80"
              ariaLabel="color-ring-loading"
              wrapperStyle={{}}
              wrapperClass="color-ring-wrapper"
              colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
            />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500">
            <p>No Items Available</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-w-[100vw] rounded-md border shadow-md border-gray-300 max-h-[40rem]">
            {/* Table */}
            <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
              <thead className="bg-gray-700 text-white sticky top-0 z-10">
                <tr>
                  <td className="p-2">#</td>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Warehouse Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Item Code
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Item Name
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Item Image
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Batch
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Shipping Mark
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide hidden sm:table-cell">
                    Opening Stock
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide hidden sm:table-cell">
                    Inward Stock
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide hidden sm:table-cell">
                    Sales Out
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Total Quantity
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Total Carton
                  </th>
                  <th className="p-2 text-left font-normal tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-100 border divide-x text-gray-700 divide-gray-200 border-b border-collapse border-gray-200 transition-colors duration-200 cursor-pointer"
                    >
                      <td className="px-2 py-0 5">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="px-2 py-0.5 font-semibold text-yellow-700">
                        {capitalizeWords(item.warehouseName)}
                      </td>
                      <td className="px-2 py-0.5 font-semibold">
                        {capitalizeWords(item.itemCode)}
                      </td>
                      <td className="px-2 py-0.5 font-semibold">
                        {capitalizeWords(item.itemName)}
                      </td>
                      <td
                        onClick={() => handleImageClick(item.itemImage)}
                        className="px-2 py-0.5"
                      >
                        {item.itemImage ? (
                          item.itemImage.includes("drive.google.com") ? (
                            <img
                              src={`https://drive.google.com/thumbnail?id=${
                                item.itemImage.split("id=")[1]
                              }`}
                              alt="Item"
                              className="size-4 object-cover rounded-full"
                            />
                          ) : (
                            <img
                              src={item.itemImage}
                              alt="Item"
                              className="size-4 object-cover rounded-full"
                            />
                          )
                        ) : (
                          <span>No image available</span>
                        )}
                      </td>
                      <td className="px-2 py-0.5 font-semibold text-blue-700">
                        {item.inwardReference}
                      </td>
                      <td className="px-2 py-0.5 font-semibold text-blue-700">
                        {item.inwardShipmentMark}
                      </td>
                      <td className="px-2 py-0.5 hidden sm:table-cell">
                        {item.openingStock}
                      </td>
                      <td className="px-2 py-0.5 hidden sm:table-cell">
                        {item.inwardStock}
                      </td>
                      <td className="px-2 py-0.5 hidden sm:table-cell">
                        {item.salesOut}
                      </td>
                      <td className="px-2 py-0.5 font-semibold text-red-700">
                        {item.inHandStockQuantity}
                      </td>
                      <td className="px-2 py-0.5">
                        {item.itemQtyCtn != null && item.itemQtyCtn !== 0
                          ? item.inHandStockQuantity >= item.itemQtyCtn
                            ? Math.floor(
                                item.inHandStockQuantity / item.itemQtyCtn
                              )
                            : 0
                          : 0}
                      </td>
                      <td className="px-2 py-0.5 text-sm text-center space-x-2">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditInventory(item)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            <HiOutlinePencil size={16} />
                          </button>
                          {userRole === "admin" && (
                            <button
                              onClick={() => handleDeleteInventory(item)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <HiOutlineTrash size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="12"
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No items found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            Showing {indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, filteredItems.length)} of{" "}
            {filteredItems.length} items
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 disabled:bg-gray-400"
            >
              Prev
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs font-medium bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 disabled:bg-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showWarehouseModal && (
        <WarehouseDetailsModal
          warehouses={warehouses}
          onClose={() => setShowWarehouseModal(false)}
          onEditWarehouse={handleEditWarehouse}
          onDeleteWarehouse={handleDeleteWarehouse}
        />
      )}

      {showAddInventoryModal && (
        <AddInventoryModal
          onClose={() => setShowAddInventoryModal(false)}
          onAddInventory={handleAddInventory}
        />
      )}

      {showAddWarehouseModal && (
        <AddWarehouseModal
          onClose={() => {
            setShowAddWarehouseModal(false);
            setIsEditing(false);
            setWarehouseToEdit(null);
          }}
          onAddWarehouse={handleAddWarehouse}
          onUpdateWarehouse={handleUpdateWarehouse}
          warehouseToEdit={warehouseToEdit}
          isEditing={isEditing}
        />
      )}

      {showTransferModal && (
        <TransferInventoryModal
          onClose={() => setShowTransferModal(false)}
          onTransfer={handleTransfer}
        />
      )}

      {showEditModal && (
        <EditInventoryModal
          item={itemToEdit}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdateInventory}
          fetchItems={fetchItems}
        />
      )}

      {isImageModalOpen && (
        <ImageModal imageUrl={selectedImage} onClose={closeImageModal} />
      )}
      <ToastContainer />
    </div>
  );
};

export default React.memo(WarehouseInventoryTable);
